import { useState } from "react";
import { ArrowUpRight, Download, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, openDownloads } from "@/lib/api";
import { Reveal, SectionEyebrow } from "@/components/Reveal";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — I'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send. Give it another shot in a minute.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#111116] border-0 border-b border-white/20 focus:border-[#D65BB4] px-4 py-3.5 text-sm outline-none transition-colors placeholder:text-white/30";

  return (
    <section id="contact" className="relative py-24 md:py-32 lg:py-40 border-t border-white/5">
      <div className="absolute bottom-0 right-0 w-[480px] h-[480px] rounded-full bg-[#D65BB4] opacity-[0.1] blur-[140px] pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <SectionEyebrow num="04" label="Contact" />
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display font-black uppercase tracking-tight leading-[0.95] mt-6 mb-6 text-[clamp(1.6rem,5vw,3.5rem)]">
            Let's make <span className="text-vice">something.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-white/60 max-w-xl mb-14">Custom beats · features · questions.</p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal delay={0.05}>
            <form onSubmit={submit} className="flex flex-col gap-8" data-testid="contact-form">
              <div>
                <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.3em] text-white/50 block mb-3">
                  Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-input-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.3em] text-white/50 block mb-3">
                  Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.3em] text-white/50 block mb-3">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell me about the record you're building…"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="group self-start flex items-center gap-3 bg-[#D65BB4] px-8 py-4 text-sm uppercase tracking-widest font-bold hover:shadow-[0_0_40px_rgba(214,91,180,0.5)] transition-shadow disabled:opacity-50"
              >
                {loading ? "Sending…" : "Send message"}
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="flex flex-col gap-10">
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-4 border-b border-white/10 pb-6">
                  <Mail size={20} className="text-[#4DBDBE] shrink-0" />
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-white/50">Email</div>
                    <a
                      href="mailto:djmiamibookings@gmail.com"
                      data-testid="contact-email-value"
                      className="font-display text-chrome font-bold text-lg mt-1 hover:text-white transition-colors inline-block"
                    >
                      djmiamibookings@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4 border-b border-white/10 pb-6">
                  <Phone size={20} className="text-[#4DBDBE] shrink-0" />
                  <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-white/50">Phone</div>
                    <a
                      href="tel:4709829699"
                      data-testid="contact-phone-value"
                      className="font-display text-chrome font-bold text-lg mt-1 hover:text-white transition-colors inline-block"
                    >
                      470.982.9699
                    </a>
                  </div>
                </div>
              </div>

              <div className="glass p-8">
                <div className="flex items-center gap-3 mb-3">
                  <Download size={18} className="text-[#D65BB4]" />
                  <h3 className="font-display font-bold uppercase text-sm tracking-widest">My Downloads</h3>
                </div>
                <p className="text-sm text-white/60 leading-relaxed mb-6">
                  Already bought a beat? Pull up any purchase anytime — just search the email you
                  checked out with.
                </p>
                <button
                  data-testid="contact-downloads-btn"
                  onClick={openDownloads}
                  className="border border-white/25 px-6 py-3 text-xs uppercase tracking-widest font-bold hover:border-[#4DBDBE] hover:text-[#4DBDBE] transition-colors"
                >
                  Find my purchase
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
